import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { posthog } from '@/lib/posthog';

interface UserProfile {
  id: string;
  user_id: string;
  name: string | null;
  profile_type: 'leigo' | 'marketing' | 'dev' | 'empresa';
  preferred_tasks: string[];
  onboarding_completed: boolean;
  has_seen_tutorial: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  credits: number;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<{ error: Error | null }>;
  register: (email: string, password: string, name: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  refreshCredits: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const userRef = useRef<User | null>(null);
  const identifiedUserIdRef = useRef<string | null>(null);

  // Fetch user profile
  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (data && !error) {
      setProfile(data as UserProfile);
    }
  };

  // Fetch user credits
  const fetchCredits = async (userId: string) => {
    const { data, error } = await supabase
      .from('credits')
      .select('balance')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (data && !error) {
      setCredits(data.balance);
    }
  };

  // Check if user is admin
  const checkAdminRole = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .single();
    
    setIsAdmin(!!data && !error);
  };

  useEffect(() => {
    const identifyUser = (authenticatedUser: User) => {
      if (identifiedUserIdRef.current === authenticatedUser.id) return;

      if (identifiedUserIdRef.current) {
        posthog.reset();
      }

      posthog.identify(authenticatedUser.id, {
        email: authenticatedUser.email,
        name: authenticatedUser.user_metadata.name,
      });
      identifiedUserIdRef.current = authenticatedUser.id;
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        userRef.current = session?.user ?? null;
        
        // Defer Supabase calls with setTimeout to avoid deadlock
        if (session?.user) {
          identifyUser(session.user);
          setTimeout(() => {
            fetchProfile(session.user.id);
            fetchCredits(session.user.id);
            checkAdminRole(session.user.id);
          }, 0);
        } else {
          if (event === 'SIGNED_OUT' && identifiedUserIdRef.current) {
            posthog.reset();
            identifiedUserIdRef.current = null;
          }
          setProfile(null);
          setCredits(0);
          setIsAdmin(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      userRef.current = session?.user ?? null;
      
      if (session?.user) {
        identifyUser(session.user);
        fetchProfile(session.user.id);
        fetchCredits(session.user.id);
        checkAdminRole(session.user.id);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Realtime subscription for credits — scoped to a private per-user channel
  // so other users cannot listen in (enforced by RLS on realtime.messages).
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`user:${user.id}`, { config: { private: true } })
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'credits', filter: `user_id=eq.${user.id}` },
        (payload: any) => {
          const newRow = payload.new;
          if (newRow) setCredits(newRow.balance);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const register = async (email: string, password: string, name: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { name }
      }
    });

    // Add user to SendPulse list (non-blocking)
    if (!error) {
      supabase.functions.invoke('add-to-sendpulse-list', {
        body: { email, name }
      }).catch((err) => console.warn('[SendPulse] Failed to add to list:', err));
    }

    return { error };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setCredits(0);
    setIsAdmin(false);
  };

  const refreshCredits = async () => {
    if (user) {
      await fetchCredits(user.id);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        credits,
        isLoading,
        isAuthenticated: !!user,
        isAdmin,
        login,
        register,
        logout,
        refreshCredits,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
